import React, { useState } from 'react';
import { useLocale } from '@platform/core';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../contexts/firebase';
import { ppaQuestionsHebrew } from '../data/ppa-questions';
import { format } from 'date-fns';

interface QuestionAnswer {
  q: string;
  a: string | 0;
}

interface ParticipantInfo {
  FirstName: string;
  LastName: string;
  CompanyName: string;
  Title: string;
  EMail: string;
  MobilePhone: string;
}

const emptyParticipant: ParticipantInfo = {
  FirstName: '',
  LastName: '',
  CompanyName: '',
  Title: '',
  EMail: '',
  MobilePhone: '',
};

export default function PPAPage() {
  const { t } = useLocale();
  const [participant, setParticipant] = useState<ParticipantInfo>({ ...emptyParticipant });
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [questions, setQuestions] = useState<QuestionAnswer[]>([]);
  const [finished, setFinished] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [textSize, setTextSize] = useState(1);

  const startQuestionnaire = () => {
    if (!participant.FirstName || !participant.LastName || !participant.CompanyName || !participant.EMail) return;
    const qs = ppaQuestionsHebrew.map((q) => ({ q, a: 0 as string | 0 }));
    setQuestions(qs);
    setShowQuestionnaire(true);
  };

  const markAnswer = (idx: number, answer: string) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], a: answer };
      return next;
    });
  };

  const finishQuestionnaire = () => {
    setFinished(true);
  };

  const submitQuestionnaire = async () => {
    setSubmitted(true);
    await addDoc(collection(db, 'ppatests'), {
      ...participant,
      Questions: questions,
      Date: format(new Date(), 'dd/MM/yyyy'),
      CreatedDate: serverTimestamp(),
      type: 'ppa',
    });
  };

  return (
    <div className="ppa-page" dir="rtl">
      <div className="ppa-header">
        <h1>🔑 {t('planning.ppa.title')}</h1>
        <p className="ppa-brand">{t('planning.ppa.brand')}</p>
      </div>

      {/* Participant Info Form */}
      {!showQuestionnaire && !finished && (
        <div className="ppa-form-card">
          <h3>{t('planning.ppa.details')}</h3>
          <div className="ppa-form-grid">
            <div className="ppa-field">
              <label>* {t('planning.ppa.firstName')}</label>
              <input
                className="ppa-input"
                required
                value={participant.FirstName}
                onChange={(e) => setParticipant((p) => ({ ...p, FirstName: e.target.value }))}
              />
            </div>
            <div className="ppa-field">
              <label>* {t('planning.ppa.lastName')}</label>
              <input
                className="ppa-input"
                required
                value={participant.LastName}
                onChange={(e) => setParticipant((p) => ({ ...p, LastName: e.target.value }))}
              />
            </div>
            <div className="ppa-field">
              <label>* {t('planning.ppa.company')}</label>
              <input
                className="ppa-input"
                required
                value={participant.CompanyName}
                onChange={(e) => setParticipant((p) => ({ ...p, CompanyName: e.target.value }))}
              />
            </div>
            <div className="ppa-field">
              <label>{t('planning.ppa.title2')}</label>
              <input
                className="ppa-input"
                value={participant.Title}
                onChange={(e) => setParticipant((p) => ({ ...p, Title: e.target.value }))}
              />
            </div>
            <div className="ppa-field">
              <label>* {t('planning.ppa.email')}</label>
              <input
                className="ppa-input"
                required
                type="email"
                value={participant.EMail}
                onChange={(e) => setParticipant((p) => ({ ...p, EMail: e.target.value }))}
              />
            </div>
            <div className="ppa-field">
              <label>{t('planning.ppa.phone')}</label>
              <input
                className="ppa-input"
                value={participant.MobilePhone}
                onChange={(e) => setParticipant((p) => ({ ...p, MobilePhone: e.target.value }))}
              />
            </div>
          </div>
          <button className="ppa-btn ppa-btn-success" onClick={startQuestionnaire}>
            {t('planning.ppa.startAssessment')}
          </button>
        </div>
      )}

      {/* Instructions */}
      {showQuestionnaire && !finished && (
        <div className="ppa-instructions">
          <p>
            <strong>{t('planning.ppa.instructionsTitle')}</strong>
          </p>
          <ol>
            <li>{t('planning.ppa.instruction1')}</li>
            <li>{t('planning.ppa.instruction2')}</li>
            <li>{t('planning.ppa.instruction3')}</li>
            <li>
              {t('planning.ppa.instruction4plus')}<br />
              {t('planning.ppa.instruction4question')}<br />
              {t('planning.ppa.instruction4minus')}
            </li>
          </ol>
          <div className="ppa-font-controls">
            {t('planning.ppa.fontSize')}:
            <button className="ppa-btn-sm" onClick={() => setTextSize((s) => s - 0.25)}>−</button>
            <span>{Math.round(textSize * 100)}%</span>
            <button className="ppa-btn-sm" onClick={() => setTextSize((s) => s + 0.25)}>+</button>
          </div>
        </div>
      )}

      {/* Questions */}
      {showQuestionnaire && !finished && (
        <>
          <div className="ppa-questions" style={{ fontSize: `${textSize}rem` }}>
            {questions.map((q, i) => (
              <div key={i} className="ppa-question">
                <span className="ppa-q-text">
                  {i + 1}) {q.q}
                </span>
                <span className="ppa-q-answers" dir="ltr">
                  {['+', '?', '-'].map((opt) => (
                    <label key={opt} className={`ppa-radio ${q.a === opt ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name={`q-${i}`}
                        value={opt}
                        checked={q.a === opt}
                        onChange={() => markAnswer(i, opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </span>
              </div>
            ))}
          </div>
          <div className="ppa-submit-section">
            <button className="ppa-btn ppa-btn-success" onClick={finishQuestionnaire}>
              {t('planning.ppa.submitForm')}
            </button>
          </div>
        </>
      )}

      {/* Confirmation */}
      {finished && !submitted && (
        <div className="ppa-form-card">
          <h3>{t('planning.ppa.confirmDetails')}</h3>
          <div className="ppa-form-grid">
            <div className="ppa-field">
              <label>{t('planning.ppa.firstName')}</label>
              <input className="ppa-input" value={participant.FirstName} readOnly />
            </div>
            <div className="ppa-field">
              <label>{t('planning.ppa.lastName')}</label>
              <input className="ppa-input" value={participant.LastName} readOnly />
            </div>
            <div className="ppa-field">
              <label>{t('planning.ppa.company')}</label>
              <input className="ppa-input" value={participant.CompanyName} readOnly />
            </div>
            <div className="ppa-field">
              <label>{t('planning.ppa.title2')}</label>
              <input className="ppa-input" value={participant.Title} readOnly />
            </div>
            <div className="ppa-field">
              <label>{t('planning.ppa.email')}</label>
              <input className="ppa-input" value={participant.EMail} readOnly />
            </div>
            <div className="ppa-field">
              <label>{t('planning.ppa.phone')}</label>
              <input className="ppa-input" value={participant.MobilePhone} readOnly />
            </div>
          </div>
          <p className="ppa-disclaimer">
            * {t('planning.ppa.disclaimer')}
          </p>
          <button className="ppa-btn ppa-btn-success" onClick={submitQuestionnaire}>
            {t('planning.ppa.confirm')}
          </button>
        </div>
      )}

      {submitted && (
        <div className="ppa-success">
          <span className="ppa-success-badge">✅ {t('planning.ppa.successMessage')}</span>
        </div>
      )}

      <style>{`
        .ppa-page { max-width: 900px; margin: 0 auto; }
        .ppa-header { text-align: center; margin-bottom: 2rem; }
        .ppa-header h1 { margin: 0; font-size: 1.5rem; }
        .ppa-brand { color: var(--text-secondary, #757575); font-size: 0.875rem; }

        .ppa-form-card { background: var(--surface-color, #fff); border: 1px solid var(--border-color, #e0e0e0); border-radius: var(--radius-lg, 12px); padding: 1.5rem; max-width: 600px; }
        .ppa-form-card h3 { margin: 0 0 1rem; }

        .ppa-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
        .ppa-field { display: flex; flex-direction: column; gap: 0.25rem; }
        .ppa-field label { font-size: 0.875rem; font-weight: 500; }
        .ppa-input { padding: 0.5rem; border: 1px solid var(--border-color, #e0e0e0); border-radius: var(--radius-md, 8px); font-size: 0.875rem; }

        .ppa-btn { padding: 0.75rem 1.5rem; border: none; border-radius: var(--radius-md, 8px); font-size: 1rem; cursor: pointer; }
        .ppa-btn-success { background: #4caf50; color: white; }
        .ppa-btn-success:hover { background: #43a047; }
        .ppa-btn-sm { padding: 0.25rem 0.5rem; border: 1px solid var(--border-color, #e0e0e0); background: var(--surface-color, #fff); border-radius: 4px; cursor: pointer; }

        .ppa-instructions { background: #fff8e1; border: 1px solid #ffe082; border-radius: var(--radius-lg, 12px); padding: 1.5rem; margin-bottom: 1rem; }
        .ppa-instructions ol { padding-inline-start: 1.5rem; }
        .ppa-font-controls { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.75rem; }

        .ppa-questions { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
        .ppa-question { display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0.75rem; border: 1px solid #ddd; gap: 0.5rem; }
        .ppa-question:hover { background: #f5f5f5; }
        .ppa-q-text { flex: 1; }
        .ppa-q-answers { display: flex; gap: 0.75rem; }
        .ppa-radio { display: flex; align-items: center; gap: 0.25rem; cursor: pointer; }
        .ppa-radio input { cursor: pointer; }
        .ppa-radio.selected span { font-weight: 700; color: var(--primary-color, #1976d2); }

        .ppa-submit-section { text-align: center; padding: 1.5rem; }
        .ppa-disclaimer { font-size: 0.875rem; color: var(--text-secondary, #757575); }
        .ppa-success { text-align: center; padding: 3rem; }
        .ppa-success-badge { background: #4caf50; color: white; padding: 0.75rem 1.5rem; border-radius: var(--radius-md, 8px); font-size: 1.25rem; }

        @media (max-width: 768px) {
          .ppa-questions { grid-template-columns: 1fr; }
          .ppa-form-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
